import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button as MuiButton,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  ChevronRight,
  Edit,
  ExpandMore,
  MoreVert
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../apiClient';
import Button from '../../components/common/Button';
import { ROUTES } from '../../constants';
import {
  deleteCategory,
  fetchCategories,
  saveCategory
} from '../../features/categoriesSlice';
import { fetchProducts } from '../../features/productsSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { Category, Product } from '../../types';

interface CategoryFormState {
  id?: string;
  name: string;
  description: string;
  parentId: string | '';
}

const emptyForm: CategoryFormState = {
  name: '',
  description: '',
  parentId: ''
};

const AdminCategoriesPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { items, loading: categoriesLoading } = useAppSelector(
    (state) => state.categories
  );
  const { items: products, loading: productsLoading } = useAppSelector(
    (state) => state.products
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [form, setForm] = useState<CategoryFormState>(emptyForm);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [treeSearch, setTreeSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [bulkSelected, setBulkSelected] = useState<Record<string, boolean>>({});
  const [sortBy, setSortBy] = useState<'name' | 'price' | 'stock'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [inlineDrafts, setInlineDrafts] = useState<
    Record<string, { price: string; stock: string }>
  >({});
  const [actionsAnchor, setActionsAnchor] = useState<null | HTMLElement>(null);
  const [seoFields, setSeoFields] = useState({
    metaTitle: '',
    metaDescription: '',
    slug: ''
  });
  const [settingsFields, setSettingsFields] = useState({
    visible: true,
    featured: false,
    imageUrl: ''
  });

  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(fetchProducts());
  }, [dispatch]);

  const { roots, byParent } = useMemo(() => {
    const map: Record<string, Category[]> = {};
    const rootNodes: Category[] = [];
    items.forEach((cat) => {
      const parentId = cat.parent?._id;
      if (parentId) {
        if (!map[parentId]) map[parentId] = [];
        map[parentId].push(cat);
      } else {
        rootNodes.push(cat);
      }
    });
    return { roots: rootNodes, byParent: map };
  }, [items]);

  useEffect(() => {
    if (!selectedId && roots[0]) {
      setSelectedId(roots[0]._id);
    }
  }, [roots, selectedId]);

  const selectedCategory = useMemo(
    () => items.find((c) => c._id === selectedId) || roots[0],
    [items, selectedId, roots]
  );

  useEffect(() => {
    if (selectedCategory) {
      setSeoFields((prev) => ({
        metaTitle: prev.metaTitle || selectedCategory.name,
        metaDescription: prev.metaDescription || selectedCategory.description,
        slug: selectedCategory.slug
      }));
    }
  }, [selectedCategory]);

  const parentOptions = useMemo(() => items, [items]);

  const productCountByCategoryId = useMemo(() => {
    const map: Record<string, number> = {};
    items.forEach((category) => {
      map[category._id] = category.productCount || 0;
    });
    return map;
  }, [items]);

  const productsInSelected = useMemo(() => {
    if (!selectedCategory) return [];
    return products.filter((p) => p.category?._id === selectedCategory._id);
  }, [products, selectedCategory]);

  const childrenOfSelected = useMemo(() => {
    if (!selectedCategory) return [];
    return items.filter((c) => c.parent?._id === selectedCategory._id);
  }, [items, selectedCategory]);

  const breadcrumb = useMemo(() => {
    if (!selectedCategory) return [];
    const byId = items.reduce<Record<string, Category>>((acc, c) => {
      acc[c._id] = c;
      return acc;
    }, {});
    const chain: Category[] = [selectedCategory];
    let current = selectedCategory;
    while (current.parent?._id && byId[current.parent._id]) {
      current = byId[current.parent._id];
      chain.unshift(current);
    }
    return chain;
  }, [items, selectedCategory]);

  const sortedProducts = useMemo(() => {
    const list = [...productsInSelected];
    list.sort((a, b) => {
      if (sortBy === 'name') {
        return sortDir === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      }
      if (sortBy === 'price') {
        return sortDir === 'asc' ? a.price - b.price : b.price - a.price;
      }
      return sortDir === 'asc' ? a.stock - b.stock : b.stock - a.stock;
    });
    return list;
  }, [productsInSelected, sortBy, sortDir]);

  const bulkSelectedIds = useMemo(
    () => Object.keys(bulkSelected).filter((id) => bulkSelected[id]),
    [bulkSelected]
  );

  const matchesSearch = (category: Category) =>
    category.name.toLowerCase().includes(treeSearch.toLowerCase());

  const subtreeMatchesSearch = (category: Category): boolean => {
    if (!treeSearch) return true;
    if (matchesSearch(category)) return true;
    const children = byParent[category._id] || [];
    return children.some((child) => subtreeMatchesSearch(child));
  };

  const handleEdit = (category: Category) => {
    setForm({
      id: category._id,
      name: category.name,
      description: category.description,
      parentId: category.parent?._id || ''
    });
    setDialogOpen(true);
  };

  const handleCreate = () => {
    setForm(emptyForm);
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    dispatch(deleteCategory(id));
    setActionsAnchor(null);
    if (selectedId === id) setSelectedId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDialogError(null);
    const result = await dispatch(
      saveCategory({
        id: form.id,
        name: form.name,
        description: form.description,
        parent: form.parentId || null
      })
    );
    if (saveCategory.rejected.match(result)) {
      setDialogError(result.payload || 'Failed to save category');
      return;
    }
    setDialogOpen(false);
    setForm(emptyForm);
    dispatch(fetchCategories());
  };

  const handleSort = (key: 'name' | 'price' | 'stock') => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(key);
    setSortDir('asc');
  };

  const handleInlineDraft = (
    id: string,
    field: 'price' | 'stock',
    value: string
  ) => {
    setInlineDrafts((prev) => ({
      ...prev,
      [id]: {
        price: prev[id]?.price ?? '',
        stock: prev[id]?.stock ?? '',
        [field]: value
      }
    }));
  };

  const saveInline = async (product: Product) => {
    const draft = inlineDrafts[product._id];
    if (!draft) return;
    const payload: { price?: number; stock?: number } = {};
    if (draft.price !== '') payload.price = Number(draft.price);
    if (draft.stock !== '') payload.stock = Number(draft.stock);
    if (Object.keys(payload).length === 0) return;
    await apiClient.put(`/products/${product._id}`, payload);
    await dispatch(fetchProducts());
  };

  const hasChildren = (id: string) => (byParent[id] || []).length > 0;

  const renderTreeNodes = (nodes: Category[], depth = 0): React.ReactNode =>
    nodes
      .filter((node) => subtreeMatchesSearch(node))
      .map((node) => {
        const children = byParent[node._id] || [];
        const isCollapsed = collapsed[node._id];
        const isSelected = node._id === selectedCategory?._id;
        return (
          <Box key={node._id}>
            <ListItemButton
              onClick={() => setSelectedId(node._id)}
              sx={{
                py: 0.5,
                pl: 1 + depth * 2,
                borderLeft: isSelected ? '3px solid' : '3px solid transparent',
                borderColor: isSelected ? 'primary.main' : 'transparent',
                bgcolor: isSelected ? 'rgba(227,91,143,0.06)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' }
              }}
            >
              {hasChildren(node._id) ? (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCollapsed((prev) => ({
                      ...prev,
                      [node._id]: !prev[node._id]
                    }));
                  }}
                  sx={{ mr: 0.5, opacity: 0.6 }}
                >
                  {isCollapsed ? (
                    <ChevronRight fontSize="small" />
                  ) : (
                    <ExpandMore fontSize="small" />
                  )}
                </IconButton>
              ) : (
                <Box sx={{ width: 28 }} />
              )}
              {depth > 0 && (
                <Typography sx={{ mr: 1, opacity: 0.4, fontSize: '0.85rem' }}>
                  ↳
                </Typography>
              )}
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: depth === 0 ? 600 : 400 }}
                  >
                    {node.name}
                  </Typography>
                }
                secondary={`(${productCountByCategoryId[node._id] || 0})`}
              />
            </ListItemButton>
            {!isCollapsed && children.length > 0 && (
              <Box>{renderTreeNodes(children, depth + 1)}</Box>
            )}
          </Box>
        );
      });

  return (
    <Box sx={{ py: 1 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            Categories
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your category tree and product mapping at scale.
          </Typography>
        </Box>
        <MuiButton
          variant="contained"
          color="primary"
          onClick={handleCreate}
          sx={{ borderRadius: 999, px: 3 }}
        >
          New category
        </MuiButton>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip label={`Total categories: ${items.length}`} size="small" variant="outlined" />
        <Chip
          label={`Top-level: ${items.filter((c) => !c.parent).length}`}
          size="small"
          variant="outlined"
        />
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '280px 1fr' },
          gap: 3
        }}
      >
        <Box
          sx={{
            borderRadius: 3,
            p: 2,
            bgcolor: '#f9fafb',
            boxShadow: '0 8px 24px rgba(0,0,0,0.04)',
            maxHeight: '72vh',
            overflow: 'auto'
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            Category tree
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="Search categories..."
            value={treeSearch}
            onChange={(e) => setTreeSearch(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          {categoriesLoading ? (
            <Box sx={{ display: 'grid', gap: 1 }}>
              <Skeleton variant="rounded" height={30} />
              <Skeleton variant="rounded" height={30} />
              <Skeleton variant="rounded" height={30} />
              <Skeleton variant="rounded" height={30} />
            </Box>
          ) : (
            <List disablePadding>{renderTreeNodes(roots)}</List>
          )}
        </Box>

        <Box>
          {categoriesLoading ? (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Skeleton variant="rounded" height={156} />
              <Skeleton variant="rounded" height={38} width={340} />
              <Skeleton variant="rounded" height={280} />
            </Box>
          ) : (
            selectedCategory && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 2
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    borderRadius: 3,
                    p: 3,
                    bgcolor: '#f9fafb',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.04)'
                  }}
                >
                  <Box sx={{ mb: 1, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {breadcrumb.map((crumb, idx) => (
                      <Typography
                        key={crumb._id}
                        component="button"
                        onClick={() => setSelectedId(crumb._id)}
                        sx={{
                          border: 0,
                          background: 'transparent',
                          p: 0,
                          m: 0,
                          cursor: 'pointer',
                          color:
                            idx === breadcrumb.length - 1
                              ? 'text.primary'
                              : 'text.secondary',
                          fontWeight: idx === breadcrumb.length - 1 ? 600 : 400,
                          fontSize: '0.8rem'
                        }}
                      >
                        {crumb.name}
                        {idx < breadcrumb.length - 1 ? ' > ' : ''}
                      </Typography>
                    ))}
                  </Box>

                  <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 600 }}>
                    {selectedCategory.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560 }}>
                    {selectedCategory.description}
                  </Typography>

                  <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={
                        selectedCategory.parent
                          ? `Parent: ${selectedCategory.parent.name}`
                          : 'Top-level category'
                      }
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`Products (incl. children): ${
                        productCountByCategoryId[selectedCategory._id] || 0
                      }`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`Subcategories: ${childrenOfSelected.length}`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={selectedCategory.isActive ? 'Active' : 'Hidden'}
                      size="small"
                      color={selectedCategory.isActive ? 'success' : 'default'}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
                    <MuiButton variant="contained" onClick={() => handleEdit(selectedCategory)}>
                      Edit Category
                    </MuiButton>
                    <MuiButton
                      variant="outlined"
                      onClick={() =>
                        navigate(`${ROUTES.ADMIN}/products?category=${selectedCategory._id}`)
                      }
                    >
                      Add Product
                    </MuiButton>
                    <MuiButton
                      variant="outlined"
                      onClick={() => {
                        setForm({
                          ...emptyForm,
                          parentId: selectedCategory._id
                        });
                        setDialogOpen(true);
                      }}
                    >
                      Add Subcategory
                    </MuiButton>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, ml: 2, mt: 0.5 }}>
                  <Tooltip title="Actions">
                    <IconButton onClick={(e) => setActionsAnchor(e.currentTarget)}>
                      <MoreVert />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={actionsAnchor}
                    open={Boolean(actionsAnchor)}
                    onClose={() => setActionsAnchor(null)}
                  >
                    <MenuItem
                      onClick={() => {
                        setActionsAnchor(null);
                        handleDelete(selectedCategory._id);
                      }}
                    >
                      Delete category
                    </MenuItem>
                  </Menu>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                <Tab label="Products" />
                <Tab label="Subcategories" />
                <Tab label="SEO" />
                <Tab label="Settings" />
              </Tabs>

              {activeTab === 0 && (
                <Box>
                  {bulkSelectedIds.length > 0 && (
                    <Box
                      sx={{
                        p: 1.5,
                        mb: 1.5,
                        borderRadius: 2,
                        bgcolor: '#f9fafb',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Typography variant="body2">
                        {bulkSelectedIds.length} selected
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <MuiButton
                          size="small"
                          onClick={async () => {
                            await Promise.all(
                              bulkSelectedIds.map((id) =>
                                apiClient.put(`/products/${id}`, { isActive: false })
                              )
                            );
                            setBulkSelected({});
                            dispatch(fetchProducts());
                          }}
                        >
                          Bulk deactivate
                        </MuiButton>
                        <MuiButton size="small" onClick={() => setBulkSelected({})}>
                          Clear
                        </MuiButton>
                      </Box>
                    </Box>
                  )}

                  {productsLoading ? (
                    <Box sx={{ display: 'grid', gap: 1 }}>
                      <Skeleton variant="rounded" height={44} />
                      <Skeleton variant="rounded" height={44} />
                      <Skeleton variant="rounded" height={44} />
                      <Skeleton variant="rounded" height={44} />
                    </Box>
                  ) : (
                    <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={
                              sortedProducts.length > 0 &&
                              bulkSelectedIds.length === sortedProducts.length
                            }
                            indeterminate={
                              bulkSelectedIds.length > 0 &&
                              bulkSelectedIds.length < sortedProducts.length
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                const all: Record<string, boolean> = {};
                                sortedProducts.forEach((p) => {
                                  all[p._id] = true;
                                });
                                setBulkSelected(all);
                              } else {
                                setBulkSelected({});
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>Thumbnail</TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={sortBy === 'name'}
                            direction={sortBy === 'name' ? sortDir : 'asc'}
                            onClick={() => handleSort('name')}
                          >
                            Name
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={sortBy === 'price'}
                            direction={sortBy === 'price' ? sortDir : 'asc'}
                            onClick={() => handleSort('price')}
                          >
                            Price
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={sortBy === 'stock'}
                            direction={sortBy === 'stock' ? sortDir : 'asc'}
                            onClick={() => handleSort('stock')}
                          >
                            Stock
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Updated</TableCell>
                        <TableCell align="right">⋯</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedProducts.map((p) => (
                        <TableRow
                          key={p._id}
                          hover
                          onClick={() => navigate(`${ROUTES.ADMIN}/products?edit=${p._id}`)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell
                            padding="checkbox"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Checkbox
                              checked={Boolean(bulkSelected[p._id])}
                              onChange={(e) =>
                                setBulkSelected((prev) => ({
                                  ...prev,
                                  [p._id]: e.target.checked
                                }))
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Avatar
                              src={p.images?.[0]}
                              variant="rounded"
                              sx={{ width: 34, height: 34 }}
                            />
                          </TableCell>
                          <TableCell>{p.name}</TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <TextField
                              size="small"
                              value={inlineDrafts[p._id]?.price ?? p.price}
                              onChange={(e) =>
                                handleInlineDraft(p._id, 'price', e.target.value)
                              }
                              onBlur={() => saveInline(p)}
                              sx={{ maxWidth: 90 }}
                            />
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <TextField
                              size="small"
                              value={inlineDrafts[p._id]?.stock ?? p.stock}
                              onChange={(e) =>
                                handleInlineDraft(p._id, 'stock', e.target.value)
                              }
                              onBlur={() => saveInline(p)}
                              sx={{ maxWidth: 72 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={p.isActive ? 'Active' : 'Draft'}
                              color={p.isActive ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            {p.updatedAt
                              ? new Date(p.updatedAt).toLocaleDateString()
                              : '-'}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit product">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`${ROUTES.ADMIN}/products?edit=${p._id}`);
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                      {sortedProducts.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8}>
                            <Box sx={{ py: 3, textAlign: 'center' }}>
                              <Typography variant="body2" color="text.secondary">
                                No products in this category yet.
                              </Typography>
                              <MuiButton
                                sx={{ mt: 1 }}
                                variant="outlined"
                                onClick={() =>
                                  navigate(
                                    `${ROUTES.ADMIN}/products?category=${selectedCategory._id}`
                                  )
                                }
                              >
                                Add Product
                              </MuiButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                    </Table>
                  )}
                </Box>
              )}

              {activeTab === 1 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Subcategories
                  </Typography>
                  {childrenOfSelected.length > 0 ? (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {childrenOfSelected.map((child) => (
                        <Chip
                          key={child._id}
                          label={`${child.name} (${productCountByCategoryId[child._id] || 0})`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No subcategories yet.
                    </Typography>
                  )}
                </Box>
              )}

              {activeTab === 2 && (
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <TextField
                    label="Meta title"
                    value={seoFields.metaTitle}
                    onChange={(e) =>
                      setSeoFields((prev) => ({ ...prev, metaTitle: e.target.value }))
                    }
                    fullWidth
                  />
                  <TextField
                    label="Meta description"
                    value={seoFields.metaDescription}
                    onChange={(e) =>
                      setSeoFields((prev) => ({
                        ...prev,
                        metaDescription: e.target.value
                      }))
                    }
                    fullWidth
                    multiline
                    minRows={3}
                  />
                  <TextField
                    label="URL slug"
                    value={seoFields.slug}
                    onChange={(e) =>
                      setSeoFields((prev) => ({ ...prev, slug: e.target.value }))
                    }
                    fullWidth
                  />
                  <Box
                    sx={{
                      p: 2,
                      border: '1px solid rgba(0,0,0,0.08)',
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Google preview
                    </Typography>
                    <Typography color="primary.main">
                      {seoFields.metaTitle || selectedCategory.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {window.location.origin}/category/{seoFields.slug || selectedCategory.slug}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {seoFields.metaDescription || selectedCategory.description}
                    </Typography>
                  </Box>
                </Box>
              )}

              {activeTab === 3 && (
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settingsFields.visible}
                        onChange={(e) =>
                          setSettingsFields((prev) => ({
                            ...prev,
                            visible: e.target.checked
                          }))
                        }
                      />
                    }
                    label="Visibility"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settingsFields.featured}
                        onChange={(e) =>
                          setSettingsFields((prev) => ({
                            ...prev,
                            featured: e.target.checked
                          }))
                        }
                      />
                    }
                    label="Featured"
                  />
                  <TextField
                    label="Category image URL"
                    value={settingsFields.imageUrl}
                    onChange={(e) =>
                      setSettingsFields((prev) => ({
                        ...prev,
                        imageUrl: e.target.value
                      }))
                    }
                    fullWidth
                  />
                  <MuiButton
                    component="label"
                    variant="outlined"
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Upload category image
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setSettingsFields((prev) => ({
                          ...prev,
                          imageUrl: URL.createObjectURL(file)
                        }));
                      }}
                    />
                  </MuiButton>
                </Box>
              )}
            </>
            )
          )}
        </Box>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth>
        <DialogTitle>{form.id ? 'Edit category' : 'New category'}</DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 1 }} onClose={() => setDialogError(null)}>
              {dialogError}
            </Alert>
          )}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              select
              label="Parent category"
              value={form.parentId}
              onChange={(e) => setForm((f) => ({ ...f, parentId: e.target.value }))}
              fullWidth
            >
              <MenuItem value="">None</MenuItem>
              {parentOptions.map((c) => (
                <MenuItem key={c._id} value={c._id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setDialogOpen(false)}>Cancel</MuiButton>
          <Button onClick={handleSubmit} type="submit" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCategoriesPage;
